import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  PawPrint, LayoutDashboard, ShoppingCart, Boxes, Users, Scissors,
  BedDouble, BarChart3, Settings, LogOut, Menu, X, Store, Bell, ShoppingBag,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import api, { fmtDateTime } from "../lib/api";

const NAV = [
  { to: "/staff", end: true, label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "cashier", "groomer", "kennel_staff"] },
  { to: "/staff/pos", label: "POS Register", icon: ShoppingCart, roles: ["admin", "manager", "cashier"] },
  { to: "/staff/inventory", label: "Inventory", icon: Boxes, roles: ["admin", "manager"] },
  { to: "/staff/customers", label: "Customers & Pets", icon: Users, roles: ["admin", "manager", "cashier", "groomer", "kennel_staff"] },
  { to: "/staff/grooming", label: "Grooming", icon: Scissors, roles: ["admin", "manager", "groomer", "cashier"] },
  { to: "/staff/boarding", label: "Boarding", icon: BedDouble, roles: ["admin", "manager", "kennel_staff", "cashier"] },
  { to: "/staff/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager"] },
  { to: "/staff/settings", label: "Settings", icon: Settings, roles: ["admin", "manager"] },
];

const KIND_ICONS = {
  order: { icon: ShoppingBag, cls: "bg-[#0E7C84]" },
  grooming: { icon: Scissors, cls: "bg-[#E84855]" },
  boarding: { icon: BedDouble, cls: "bg-[#2A9D8F]" },
};

function NotifBell({ alerts, unread, onOpenAlert, onReadAll, dark = false }) {
  const [bellOpen, setBellOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setBellOpen(!bellOpen)} data-testid="notif-bell-btn"
        className={`relative p-2 rounded-xl transition-colors ${dark ? "hover:bg-white/10" : "hover:bg-black/5"}`}>
        <Bell className={`w-5 h-5 ${dark ? "text-white" : "text-slate-600"}`} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#E84855] text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1" data-testid="notif-unread-badge">
            {unread}
          </span>
        )}
      </button>
      {bellOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white rounded-2xl border border-[#BFE8E9] shadow-xl z-50 overflow-hidden" data-testid="notif-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2F5F5]">
              <p className="font-display font-semibold text-sm text-slate-800">Notifications</p>
              {unread > 0 && (
                <button className="text-xs text-[#0E7C84] font-semibold hover:underline" onClick={onReadAll} data-testid="notif-read-all-btn">
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {alerts.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No notifications yet.</p>}
              {alerts.map((a) => {
                const k = KIND_ICONS[a.kind] || KIND_ICONS.order;
                return (
                  <button key={a.id} onClick={() => { onOpenAlert(a); setBellOpen(false); }} data-testid={`notif-item-${a.id.slice(0, 8)}`}
                    className={`w-full text-left px-4 py-3 border-b border-[#E2F5F5] hover:bg-[#F2FBFB] transition-colors ${!a.read ? "bg-[#D9F7F8]/40" : ""}`}>
                    <div className="flex gap-3">
                      <span className={`w-8 h-8 rounded-lg ${k.cls} flex items-center justify-center shrink-0`}>
                        <k.icon className="w-4 h-4 text-white" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                          <span className="truncate">{a.title}</span>
                          {!a.read && <span className="w-2 h-2 rounded-full bg-[#E84855] shrink-0" />}
                        </p>
                        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{a.body}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{fmtDateTime(a.created_at)}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const ROLE_LABELS = { admin: "Admin", manager: "Manager", cashier: "Cashier", groomer: "Groomer", kennel_staff: "Kennel Staff" };

export default function StaffLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadAlerts = () => {
    api.get("/notifications/alerts")
      .then((r) => { setAlerts(r.data.alerts); setUnread(r.data.unread); })
      .catch(() => {});
  };

  useEffect(() => {
    loadAlerts();
    const t = setInterval(loadAlerts, 30000);
    return () => clearInterval(t);
  }, []);

  const openAlert = async (a) => {
    try { await api.post(`/notifications/alerts/${a.id}/read`); } catch {}
    loadAlerts();
    if (a.link) navigate(a.link);
  };

  const readAll = async () => {
    try { await api.post("/notifications/alerts/read-all"); } catch {}
    loadAlerts();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-2 border-b border-white/10">
        <img src="/logo-icon.png" alt="Pet's Pantry logo" className="w-10 h-10 rounded-xl" />
        <div>
          <p className="font-display font-bold text-white leading-tight">Pet's Pantry</p>
          <p className="text-[11px] text-emerald-200/70">Staff Console</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`staff-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-[#F4A261] text-[#093F46]" : "text-emerald-100/80 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-white/10">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-xs text-emerald-200/70 hover:text-white mb-3" data-testid="staff-view-store">
          <Store className="w-3.5 h-3.5" /> View storefront
        </button>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-white">{user?.name}</p>
            <Badge className="bg-white/15 text-emerald-50 hover:bg-white/15 text-[10px] mt-0.5" data-testid="staff-role-badge">
              {ROLE_LABELS[user?.role] || user?.role}
            </Badge>
          </div>
          <button onClick={handleLogout} className="text-emerald-200/70 hover:text-white" data-testid="staff-logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F2FBFB]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-[#093F46] z-40">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#093F46]">{sidebar}</aside>
          <button className="absolute top-4 left-68 ml-64 text-white" onClick={() => setOpen(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="lg:hidden sticky top-0 z-30 bg-[#093F46] px-4 h-14 flex items-center justify-between">
          <button onClick={() => setOpen(true)} className="text-white" data-testid="staff-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-display font-bold text-white">Pet's Pantry</span>
          <NotifBell alerts={alerts} unread={unread} onOpenAlert={openAlert} onReadAll={readAll} dark />
        </header>
        <header className="hidden lg:flex sticky top-0 z-30 bg-[#F2FBFB]/90 backdrop-blur border-b border-[#BFE8E9] px-8 h-14 items-center justify-end">
          <NotifBell alerts={alerts} unread={unread} onOpenAlert={openAlert} onReadAll={readAll} />
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
